import { createModel } from "@rematch/core";
import { api } from "api";
import {
  ApiRolePermissionsUpdateUserRoleRelationsPutRequest,
  GetUserRoleRelationsQuery,
  RoleListVm,
  SimpleUserForUserRoleRelationVm,
  SimpleUserForUserRoleRelationVmPaginatedSearchResponse,
  UserRoleRelationVm,
} from "api/generated/lumen";
import { RootModel } from "app/store";
import { mapToGetAllQuery } from "utils";
import {
  ListRequestParams,
  mergeListParams,
  mergeListParamsWithPagination,
  OrderByType,
} from "widgets/table/useTableUtils";

export type GetUserRoleRelationsRequestParams =
  ListRequestParams<GetUserRoleRelationsQuery>;

interface UsersAndRolesAssignState {
  relations?: UserRoleRelationVm[] | null;
  listParams: GetUserRoleRelationsRequestParams;
  users: SimpleUserForUserRoleRelationVm[];
  roles?: RoleListVm[] | null;
  filterRoleId?: string | null;
  isSaveDisabled: boolean;
}

const initialState: UsersAndRolesAssignState = {
  roles: [],
  relations: [],
  listParams: {
    pageSize: 10,
    order: [
      {
        orderBy: "name",
        orderByType: OrderByType.Ascending,
      },
    ],
  },
  users: [],
  filterRoleId: "",
  isSaveDisabled: true,
};

export const usersAndRolesAssign = createModel<RootModel>()({
  state: initialState,
  reducers: {
    resetListParams(state) {
      state.listParams = {
        ...initialState.listParams,
        order: initialState.listParams.order,
      };
      state.filterRoleId = "";
    },
    setUserRoleRelations(
      state,
      payload: UserRoleRelationVm[] | undefined | null
    ) {
      state.relations = payload;
    },
    setRoles(state, payload: RoleListVm[] | undefined | null) {
      state.roles = payload;
    },
    setFilterRoleId(state, payload: string | null) {
      state.filterRoleId = payload;
    },
    setUserList(
      state,
      {
        data,
        listParams,
      }: {
        data: SimpleUserForUserRoleRelationVm[];
        listParams: GetUserRoleRelationsRequestParams;
      }
    ) {
      state.users = data;
      state.listParams = listParams;
    },
    setIsSaveDisabled(state, payload: boolean) {
      state.isSaveDisabled = payload;
    },
    reset: () => initialState,
  },

  effects: (dispatch) => {
    const { usersAndRolesAssign } = dispatch;

    return {
      async getUserRoleRelations(
        params: GetUserRoleRelationsRequestParams = {},
        state
      ): Promise<void> {
        const mergedParams = mergeListParams(
          state.usersAndRolesAssign.listParams,
          {
            ...params,
            filter: {
              ...params.filter,
              roleId: state.usersAndRolesAssign.filterRoleId,
            },
            order: [
              {
                orderBy: "name",
                orderByType: OrderByType.Ascending,
              },
            ],
          }
        );

        const { userIds, roleId, ...filters } = mergedParams.filter ?? {};

        const getAllQuery = mapToGetAllQuery({
          ...mergedParams,
          filter: filters,
        });
        const data =
          await api.lumen.rolePermissions.apiRolePermissionsGetUserRoleRelationsPost(
            {
              getUserRoleRelationsQuery: {
                ...getAllQuery,
                roleId,
                userIds,
              },
            }
          );

        const { users, roles, userRoleRelations } = data;

        dispatch(usersAndRolesAssign.setUserRoleRelations(userRoleRelations));
        dispatch(usersAndRolesAssign.setRoles(roles));

        const { items, ...pagination } =
          users as SimpleUserForUserRoleRelationVmPaginatedSearchResponse;

        dispatch(
          usersAndRolesAssign.setUserList({
            data: items || [],
            listParams: mergeListParamsWithPagination(mergedParams, pagination),
          })
        );
      },

      async updateUserRoleRelations(
        updateUserRoleRelationsCommand: ApiRolePermissionsUpdateUserRoleRelationsPutRequest
      ) {
        await api.lumen.rolePermissions.apiRolePermissionsUpdateUserRoleRelationsPut(
          updateUserRoleRelationsCommand
        );
      },
    };
  },
});
